import { useEffect, useRef, useState } from 'react';
import { Modal, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { ExternalLink, TrendingUp, TrendingDown, Copy } from 'lucide-react';
import { openCoinMarketCap } from '../utils/coinMarketUtils';
import { formatBalance, formatCryptoBalance, formatUSDBalance } from '../utils/formatBalance';

function CoinDetailsModal({ show, onHide, coin, chain }) {
    const chartContainerRef = useRef(null);
    const [selectedTimeframe, setSelectedTimeframe] = useState('1M');

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        // You could add a toast notification here
    };

    const formatPrice = (price) => {
        return parseFloat(price || 0).toFixed(2);
    };

    const formatChange = (change) => {
        return parseFloat(change || 0).toFixed(2);
    };

    // Get button styles based on whether it's selected
    const getButtonStyle = (timeframe) => {
        const isSelected = selectedTimeframe === timeframe;
        return {
            backgroundColor: isSelected ? 'var(--accent-color)' : 'var(--bg-secondary)',
            border: isSelected ? '1px solid var(--accent-color)' : '1px solid var(--border-color)',
            color: isSelected ? '#ffffff' : 'var(--text-primary)',
            fontSize: '11px',
            padding: '4px 8px',
            transition: 'all 0.2s ease'
        };
    };

    // Handle timeframe change
    const handleTimeframeChange = (timeframe) => {
        setSelectedTimeframe(timeframe);
    };

    // Get description text based on timeframe
    const getTimeframeDescription = (timeframe) => {
        const descriptions = {
            '1D': '1-day price movement',
            '1W': '1-week price movement',
            '1M': '1-month price movement',
            '1Y': '1-year price movement',
            'ALL': 'All-time price movement'
        };
        return descriptions[timeframe] || '1-month price movement';
    };

    // Get dateRange parameter for TradingView based on timeframe
    const getDateRange = (timeframe) => {
        const ranges = {
            '1D': '1D',
            '1W': '1W',
            '1M': '1M',
            '1Y': '12M',
            'ALL': '60M'
        };
        return ranges[timeframe] || '1M';
    };

    // Get TradingView symbol - try to map common symbols
    const getTradingViewSymbol = (coin) => {
        const symbolMap = {
            'BTC': 'BTCUSD',
            'ETH': 'ETHUSD',
            'USDT': 'USDTUSD',
            'USDC': 'USDCUSD',
            'BNB': 'BNBUSD',
            'ADA': 'ADAUSD',
            'SOL': 'SOLUSD',
            'XRP': 'XRPUSD',
            'DOT': 'DOTUSD',
            'DOGE': 'DOGEUSD',
            'AVAX': 'AVAXUSD',
            'SHIB': 'SHIBUSD',
            'MATIC': 'MATICUSD',
            'LTC': 'LTCUSD',
            'UNI': 'UNIUSD',
            'LINK': 'LINKUSD',
            'ATOM': 'ATOMUSD'
        };

        return symbolMap[coin?.symbol?.toUpperCase()] || `${coin?.symbol?.toUpperCase() || 'BTC'}USD`;
    };

    // Initialize TradingView widget with light theme for better text visibility
    useEffect(() => {
        if (show && coin && chartContainerRef.current) {
            // Clear previous widget
            chartContainerRef.current.innerHTML = '';

            const script = document.createElement('script');
            script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-symbol-overview.js';
            script.type = 'text/javascript';
            script.async = true;
            script.innerHTML = JSON.stringify({
                symbols: [[`BINANCE:${getTradingViewSymbol(coin)}|${getDateRange(selectedTimeframe)}`]],
                chartOnly: true,
                width: "100%",
                height: "220",
                locale: "en",
                colorTheme: "light",
                autosize: true,
                showVolume: false,
                showMA: false,
                hideDateRanges: true,
                hideMarketStatus: true,
                hideSymbolLogo: true,
                scalePosition: "right",
                scaleMode: "Normal",
                fontFamily: "-apple-system, BlinkMacSystemFont, Trebuchet MS, Roboto, Ubuntu, sans-serif",
                fontSize: "10",
                noTimeScale: false,
                valuesTracking: "1",
                changeMode: "price-and-percent",
                trendLineColor: "rgba(41, 98, 255, 1)",
                underLineColor: "rgba(41, 98, 255, 0.3)",
                underLineBottomColor: "rgba(41, 98, 255, 0)",
                isTransparent: true,
                backgroundColor: "rgba(255, 255, 255, 0)",
                gridLineColor: "rgba(128, 128, 128, 0.2)",
                textColor: "#555555"
            });

            chartContainerRef.current.appendChild(script);
        }
    }, [show, coin, selectedTimeframe]);

    // Early return after hooks
    if (!coin) return null;

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered={true}
            size="lg"
            className="custom--modal coin-details-modal d-flex align-items-center justify-content-center"
            style={{ display: 'flex !important', zIndex: 99999 }}
            dialogClassName="modal-dialog-fixed-width"
            backdropClassName="modal-backdrop-high-z"
        >
            {/* Header with dark mode support */}
            <div className="modal-header m-0" style={{ backgroundColor: 'var(--bg-primary)', borderBottom: '1px solid var(--border-color)' }}>
                <div className="d-flex align-items-center gap-3 w-100">
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip id="coin-image-tooltip">{coin.name} Logo</Tooltip>}
                    >
                        <img
                            src={coin.image}
                            alt={coin.name}
                            className="rounded-circle"
                            style={{
                                width: '48px',
                                height: '48px',
                                border: '2px solid var(--border-color)'
                            }}
                        />
                    </OverlayTrigger>
                    <div className="flex-grow-1">
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id="coin-name-tooltip">{coin.name} ({coin.symbol})</Tooltip>}
                        >
                            <h4 className="mb-1 fw-bold" style={{ color: 'var(--text-primary)' }}>
                                {coin.name}
                            </h4>
                        </OverlayTrigger>
                        <div className="d-flex align-items-center gap-2">
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip id="coin-symbol-tooltip">Symbol: {coin.symbol}</Tooltip>}
                            >
                                <span
                                    className="badge px-3 py-1 rounded-pill"
                                    style={{
                                        backgroundColor: 'var(--bg-secondary)',
                                        color: 'var(--text-primary)',
                                        border: '1px solid var(--border-color)'
                                    }}
                                >
                                    {coin.symbol}
                                </span>
                            </OverlayTrigger>
                            <span style={{ color: 'var(--text-secondary)' }} className="small">
                                • {chain?.name}
                            </span>
                        </div>
                    </div>
                    <div className="text-end">
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id="coin-price-tooltip">Current Price: ${formatPrice(coin.price?.price)}</Tooltip>}
                        >
                            <div className="h4 mb-0 fw-bold" style={{ color: 'var(--text-primary)' }}>
                                ${formatPrice(coin.price?.price)}
                            </div>
                        </OverlayTrigger>
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id="coin-change-tooltip">24h Change: {coin.price?.change >= 0 ? '+' : ''}{formatChange(coin.price?.change)}%</Tooltip>}
                        >
                            <div className={`small fw-semibold ${coin.price?.change >= 0 ? 'text-success' : 'text-danger'}`}>
                                {coin.price?.change >= 0 ? '+' : ''}{formatChange(coin.price?.change)}%

                            </div>
                        </OverlayTrigger>
                    </div>
                </div>
                <button className="close-button" onClick={onHide} style={{ color: 'var(--text-primary)' }}>
                    ✕
                </button>
            </div>

            <Modal.Body style={{ backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)', padding: '32px' }}>
                {/* Stats Cards */}
                <div className="row g-4 mb-4">
                    <div className="col-md-6">
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id="balance-tooltip">Your current {coin.symbol} balance: {coin.balance}</Tooltip>}
                        >
                            <div
                                className="card border-0 shadow-sm h-100"
                                style={{
                                    backgroundColor: 'var(--bg-secondary)',
                                    border: '1px solid var(--border-color)'
                                }}
                            >
                                <div className="card-body" style={{ padding: '20px' }}>
                                    <h6
                                        className="card-subtitle mb-2 small text-uppercase"
                                        style={{ color: 'var(--text-secondary)' }}
                                    >
                                        Your Balance
                                    </h6>
                                    <h3 className="card-title mb-0 fw-bold" style={{ color: 'var(--text-primary)' }}>
                                        {formatCryptoBalance(coin.balance, coin.symbol)}
                                    </h3>
                                </div>
                            </div>
                        </OverlayTrigger>
                    </div>
                    <div className="col-md-6">
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id="usd-value-tooltip">USD equivalent: {formatUSDBalance(coin.balance * coin.price?.price || 0)}</Tooltip>}
                        >
                            <div
                                className="card border-0 shadow-sm h-100"
                                style={{
                                    backgroundColor: 'var(--bg-secondary)',
                                    border: '1px solid var(--border-color)'
                                }}
                            >
                                <div className="card-body" style={{ padding: '20px' }}>
                                    <h6
                                        className="card-subtitle mb-2 small text-uppercase"
                                        style={{ color: 'var(--text-secondary)' }}
                                    >
                                        USD Value
                                    </h6>
                                    <h3 className="card-title mb-0 fw-bold" style={{ color: 'var(--text-primary)' }}>
                                        {formatUSDBalance(coin.balance * coin.price?.price || 0)}
                                    </h3>
                                </div>
                            </div>
                        </OverlayTrigger>
                    </div>
                </div>

                {/* Chart Card with Time Filters */}
                <div
                    className="card border-0 shadow-sm mb-4"
                    style={{
                        backgroundColor: 'var(--bg-secondary)',
                        border: '1px solid var(--border-color)'
                    }}
                >
                    <div
                        className="card-header border-0 py-3 d-flex justify-content-between align-items-center"
                        style={{
                            backgroundColor: 'var(--bg-secondary)',
                            borderBottom: '1px solid var(--border-color)'
                        }}
                    >
                        <div>
                            <h6 className="mb-0 fw-semibold" style={{ color: 'var(--text-primary)' }}>
                                Price Chart
                            </h6>
                            <small style={{ color: 'var(--text-secondary)' }}>
                                {getTimeframeDescription(selectedTimeframe)} • Powered by TradingView
                            </small>
                        </div>
                        <div className="d-flex gap-1">
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip id="1d-tooltip">1 Day Chart</Tooltip>}
                            >
                                <button
                                    className="btn btn-sm"
                                    style={getButtonStyle('1D')}
                                    onClick={() => handleTimeframeChange('1D')}
                                >
                                    1D
                                </button>
                            </OverlayTrigger>
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip id="1w-tooltip">1 Week Chart</Tooltip>}
                            >
                                <button
                                    className="btn btn-sm"
                                    style={getButtonStyle('1W')}
                                    onClick={() => handleTimeframeChange('1W')}
                                >
                                    1W
                                </button>
                            </OverlayTrigger>
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip id="1m-tooltip">1 Month Chart</Tooltip>}
                            >
                                <button
                                    className="btn btn-sm"
                                    style={getButtonStyle('1M')}
                                    onClick={() => handleTimeframeChange('1M')}
                                >
                                    1M
                                </button>
                            </OverlayTrigger>
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip id="1y-tooltip">1 Year Chart</Tooltip>}
                            >
                                <button
                                    className="btn btn-sm"
                                    style={getButtonStyle('1Y')}
                                    onClick={() => handleTimeframeChange('1Y')}
                                >
                                    1Y
                                </button>
                            </OverlayTrigger>
                        </div>
                    </div>
                    <div className="card-body p-0">
                        <div
                            ref={chartContainerRef}
                            style={{
                                height: '220px',
                                width: '100%',
                                minWidth: '500px',
                                maxWidth: '500px',
                                backgroundColor: 'transparent',
                                overflow: 'hidden'
                            }}
                        >
                        </div>
                    </div>
                </div>


            </Modal.Body>

            <Modal.Footer
                style={{
                    backgroundColor: 'var(--bg-primary)',
                    borderTop: '1px solid var(--border-color)'
                }}
            >
                <div className="d-flex gap-2 w-100">
                    <Button
                        variant="outline-secondary"
                        onClick={onHide}
                        style={{
                            borderColor: 'var(--border-color)',
                            color: 'var(--text-primary)'
                        }}
                    >
                        Close
                    </Button>
                    <Button
                        variant="primary"
                        className="flex-grow-1 d-flex align-items-center justify-content-center gap-2"
                        onClick={() => openCoinMarketCap(coin)}
                        style={{
                            backgroundColor: 'var(--accent-color)',
                            borderColor: 'var(--accent-color)',
                            color: '#ffffff',
                            fontWeight: '500'
                        }}
                    >
                        <ExternalLink size={18} />
                        View on CoinMarketCap
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
}

export default CoinDetailsModal;